import { IDataBinder, IFormValidationResult } from '@rocketmakers/armstrong';
import { FormBinderKey } from '@rocketmakers/armstrong/dist/components/form/propertyPathBuilder';
import * as React from 'react';
import { InputState, InputValidator } from './ValidationTypes';

export function validationResultEquals(a: IFormValidationResult, b: IFormValidationResult): boolean {
  return a.attribute === b.attribute && a.message === b.message;
}

/**
 * Calculate the form validity. This needs to happen separately to user interaction on the form as errors are not shown until
 * the input has been touched.
 * @param dataBinder the form state
 * @param inputValidators  the form validators
 */
export function calculateFormValidity<T>(dataBinder: IDataBinder<T>, inputValidators: InputValidator<T>[]): boolean {
  for (const inputValidator of inputValidators) {
    for (const validator of inputValidator.validators) {
      if (!validator.validate(dataBinder.getKeyValue(inputValidator.key as any), dataBinder)) {
        return false;
      }
    }
  }
  return true;
}

/**
 * The FormBinderKey can be in a number of different formats. We need to convert that format that it is in
 * to dot notation so that the array of attributes (in dot notation) and messages can be passed to the DataForm.
 * @param key the key to convert
 * @returns the key in dot notation
 */
function convertFormBinderKeyToDotNotation<T>(key: FormBinderKey<T>): string {
  // Check if the key is a keyof T, then can return it as a string
  if (typeof key === 'string') {
    return key as string;
  }

  throw new Error('Nested forms not supported');

  // Otherwise, convert the key to a string. It'll have a format like:
  // function (b) { return b.prop('parentField').index(0).prop('childField'); }
  // Means that the function needs to have the field name as a string rather than as a variable, which isn't great,
  // but is okay for now.
  // Need this to become 'parentField.0.childField'
  // const keyAsString = key.toString();

  // Split the function string by numbers and text after quotations:
  // const regexToGrabParts = /\(([0-9]+)|(?:'|"|`)([a-zA-Z0-9]+)/g;
  // const dotNotationKey = keyAsString.match(regexToGrabParts).join('.')
  // console.log("Got dot notication key", {dotNotationKey})
  // return dotNotationKey;
}

/**
 * Create an immutable validation results store. This holds all possible validation errors on the form.
 * If an error is being added to the errors list, the error object must come from this store. That way
 * object references can be checked against, the errors list is guaranteed to never hold duplicate
 * errors, and existing errors can be removed easily.
 *
 * @param inputValidators form validators.
 */
function createValidationResultStore<T>(inputValidators: InputValidator<T>[]): IFormValidationResult[] {
  const allResults: IFormValidationResult[] = [];
  for (const inputValidator of inputValidators) {
    for (const validator of inputValidator.validators) {
      const result: IFormValidationResult = {
        attribute: convertFormBinderKeyToDotNotation(inputValidator.key),
        message: validator.message,
      };
      Object.freeze(result);
      allResults.push(result);
    }
  }

  Object.freeze(allResults);
  return allResults;
}

/**
 * Hook to enable real time validation of an armstrong form. The form validation is defined in `inputValidators`, and
 * the form state in `dataBinder`. This returns an object containing the following:
 *  - `setTouched`: a method provided to tell when a form input control has been interacted with by the user. This must
 * be used on each input with validation. As an example:
 *
 * ```
 * <TextInput {...bind.text('city')} label="City" onBlur={setTouched} />
 * ```
 *
 *  - `formIsValid`: a boolean flag to denote if the form is valid or invalid. This doesn't rely on user interaction, so
 * is independant of error messages displayed on the form.
 *  - `validationErrors`: an array populated with the errors to display on the form. Should be passed to the `DataForm` component:
 *
 * ```
 * <DataForm validationResults={validationErrors} validationMode="both">
 * ```
 *
 * @param inputValidators form validation
 * @param dataBinder form state
 */
export function useValidatedForm<T>(inputValidators: InputValidator<T>[], dataBinder: IDataBinder<T>) {
  const [validationErrors, setValidationErrors] = React.useState<Set<IFormValidationResult>>(new Set());
  const [inputStates, setInputStates] = React.useState<InputState<T>[]>(
    inputValidators.map(input => ({
      key: input.key,
      touched: false,
    }))
  );
  const [validationResultStore] = React.useState<IFormValidationResult[]>(createValidationResultStore(inputValidators));

  const setTouched = (inputElement: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const inputStatesCopy = [...inputStates];

    const touchedInput = inputStatesCopy.find(
      input => convertFormBinderKeyToDotNotation(input.key) === inputElement.target.name
    );

    if (touchedInput) {
      touchedInput.touched = true;
      setInputStates(inputStatesCopy);
    }
  };

  const formIsValid = calculateFormValidity(dataBinder, inputValidators);

  const runValidationOnInput = (input: InputValidator<T>) => {
    const value = dataBinder.getKeyValue(input.key as any);
    const validationErrorsCopy = new Set<IFormValidationResult>(validationErrors);

    for (const validator of input.validators) {
      const immutableValidationResult = validationResultStore.find(
        result =>
          result.attribute === convertFormBinderKeyToDotNotation(input.key) && result.message === validator.message
      );

      if (validator.validate(value, dataBinder)) {
        validationErrorsCopy.delete(immutableValidationResult);
      } else {
        validationErrorsCopy.add(immutableValidationResult);
      }
    }

    setValidationErrors(validationErrorsCopy);
  };

  // Use this to calculate if this is the first render. If it is, then don't run the validation useEffect as it'll show all of the validation errors prematurely.
  const firstRenderCheck = React.useRef(0);

  // Run validation on individual inputs whenever they change value.
  for (const inputValidator of inputValidators) {
    const inputValidatorInputState = inputStates.find(is => is.key === inputValidator.key);
    let deps = [dataBinder.getKeyValue(inputValidator.key as any), inputValidatorInputState.touched];

    if (Array.isArray(inputValidator.watchKeys) && inputValidator.watchKeys.length > 0) {
      deps = [...deps, ...inputValidator.watchKeys.map(key => dataBinder.getKeyValue(key as any))];
    }

    React.useEffect(() => {
      if (firstRenderCheck.current < inputValidators.length) {
        firstRenderCheck.current++;
        return;
      }
      if (inputValidatorInputState.touched) {
        runValidationOnInput(inputValidator);
      }
    }, deps);
  }

  // Run validation on individual inputs whenever their state changes to 'touched'.
  // This covers the scenario where a user clicks on an input, doesn't change anything, and clicks off.
  for (const inputState of inputStates) {
    React.useEffect(() => {
      if (inputState.touched) {
        runValidationOnInput(inputValidators.find(validator => validator.key === inputState.key));
      }
    }, [inputState.touched]);
  }

  return {
    setTouched,
    formIsValid,
    validationErrors: Array.from(validationErrors),
  };
}
